<template>
  <div class="greeting-container is-styled">
    <div v-if="loading" class="loading-container">
      <ContentLoader
        :speed="2"
        primaryColor="#ecebeb"
        secondaryColor="#d9d9d9"
        viewBox="0 0 400 15"
      >
        <rect height="15" rx="5" ry="5" width="300" x="0" y="0" />
      </ContentLoader>
    </div>
    <h1 v-else v-dompurify-html="greeting" />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { type NaturalPersonName, type Person, ResponseError } from 'partner-api'
import { partnerApi } from '@/stores/partnerApiClient'
import { isNaturalPerson } from '@/utils/PartnerUtils'
import { getPartnerProxyId } from '@/utils/KeycloakUtils'
import { ContentLoader } from 'vue-content-loader'

const { t } = useI18n()

const loading = ref(true)
const greeting = ref('')

onMounted(async () => {
  const partnerProxyId = getPartnerProxyId()
  if (!partnerProxyId) {
    greeting.value = getGreetingMessage()
    console.error('Error: No partner proxy id available!')
    loading.value = false
    return
  }

  try {
    const response = await partnerApi().getPartner({ partnerProxyId })
    greeting.value = getGreetingMessage(response.data?.person)
  } catch (err) {
    greeting.value = getGreetingMessage()
    if (!(err instanceof ResponseError)) {
      console.error('Error while loading partner greeting:', err)
    }
  } finally {
    loading.value = false
  }
})

function getGreetingMessage(person?: Person): string {
  if (!isNaturalPerson(person) || !person.name) {
    return String(t('partnerGreeting.generalGreeting'))
  }

  const naturalPersonName: NaturalPersonName = person.name
  sessionStorage.setItem('firstName', naturalPersonName.firstName ?? '')
  sessionStorage.setItem('lastName', naturalPersonName.name ?? '')
  const message: string = [
    naturalPersonName.academicTitle,
    naturalPersonName.nobilityTitle,
    naturalPersonName.firstName,
    naturalPersonName.name,
    naturalPersonName.suffix
  ]
    .join(' ')
    .trim()

  return String(t('partnerGreeting.personGreeting', { message }))
}
</script>

<style lang="scss" scoped>
.greeting-container {
  position: relative;
}

.is-styled.overlay--disabled {
  position: absolute;
  bottom: 0;
  margin-bottom: 16px;
  width: 100%;
  min-height: 40px;
}

.greeting-container h1,
.loading-container {
  margin-bottom: var(--h1-partner-greeting-m-b);
}
</style>
